import { useEffect, useState } from "react";
import { useCompactThread, useThreadContext, type ThreadContext } from "@/api/queries";
import { Button } from "@/components/ui/Button";
import { CompressIcon, GaugeIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * The composer's context meter (PRD FR-3.4).
 *
 * It is honest about what it can know, which is the whole design. Ming does not
 * ship five tokenisers — the one that matters belongs to whichever provider is
 * answering — so this is characters divided by a ratio, and it says so. The
 * ratio starts at a documented default and becomes *this model's own, measured
 * on this conversation* as soon as one turn has reported its token count.
 *
 * A model whose window nobody publishes gets a token count and no percentage.
 * A bar with an invented denominator would look more trustworthy than the
 * estimate behind it, which is the one thing this must not do.
 */

const DEBOUNCE_MS = 400;

function compact(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${Math.round(value / 1_000)}K`;
  return String(value);
}

const BASIS_HINT: Record<string, string> = {
  calibrated: "Estimated from this conversation's measured tokens-per-character.",
  heuristic: "Estimated from character count — no measured token count yet this conversation.",
};

const WINDOW_HINT: Record<string, string> = {
  provider: "window reported by the provider",
  dataset: "window from the maintained model list",
  administrator: "window set in the model catalog",
  unknown: "this provider does not publish a context window",
};

function Bar({ percent, warn }: { percent: number; warn: boolean }) {
  return (
    <div className="bg-muted rounded-full h-1.5 w-24 overflow-hidden" aria-hidden="true">
      <div
        className={cn("h-full motion-medium transition-[width]", warn ? "bg-warning" : "bg-accent")}
        style={{ width: `${Math.min(100, Math.max(2, percent))}%` }}
      />
    </div>
  );
}

export function ContextMeter({
  threadId,
  draft,
  modelRef,
  busy,
}: {
  threadId: string;
  draft: string;
  modelRef: string;
  busy: boolean;
}) {
  // Debounced so a long question does not fire a request per keystroke; the
  // meter is a running estimate, not a character counter.
  const [settled, setSettled] = useState(draft);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(draft), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft]);

  const { data } = useThreadContext(threadId, settled, modelRef, Boolean(threadId) && !busy);
  const compactThread = useCompactThread(threadId);
  if (!data) return null;

  const context: ThreadContext = data;
  const title = [
    BASIS_HINT[context.basis] ?? "",
    `${context.prompt_chars.toLocaleString()} characters at ~${context.chars_per_token} per token.`,
    WINDOW_HINT[context.window_source] ?? "",
    // The projection is what this conversation would *open* with. A run that
    // calls tools re-sends that prompt on every round trip and spends several
    // times as much, so the measured figure belongs beside it rather than
    // instead of it.
    context.last_input_tokens
      ? `The last answer measured ${context.last_input_tokens.toLocaleString()} input tokens across ${context.last_requests} request${context.last_requests === 1 ? "" : "s"}.`
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span
        title={title}
        className={cn(
          "type-caption inline-flex items-center gap-1.5",
          context.warn ? "text-warning" : "text-ink-disabled",
        )}
      >
        <GaugeIcon className="size-3" />
        {context.window ? (
          <>
            <Bar percent={context.percent ?? 0} warn={context.warn} />
            <span className="tabular-nums">
              ~{compact(context.used_tokens)} / {compact(context.window)} ({context.percent}%)
            </span>
          </>
        ) : (
          <span className="tabular-nums">~{compact(context.used_tokens)} tokens · window unknown</span>
        )}
        <span>· estimated</span>
      </span>

      {context.last_input_tokens ? (
        <span
          className="type-caption text-ink-disabled tabular-nums"
          title="Measured, not estimated — what the provider reported for the previous answer, summed across its tool round trips."
        >
          · last answer {compact(context.last_input_tokens)} in
        </span>
      ) : null}

      {context.has_summary ? (
        <span className="type-caption text-ink-disabled" title="Earlier turns are represented by a pinned briefing.">
          · condensed through turn {context.compacted_through_seq}
        </span>
      ) : null}

      {context.warn && context.turns > 1 ? (
        <Button
          size="sm"
          variant="ghost"
          icon={<CompressIcon className="size-3.5" />}
          loading={compactThread.isPending}
          // Swallowed until backlog item 11: a condense that failed looked
          // exactly like one that was never asked for. `mutate` never
          // rejects, so the mutation cache is the only thing that has to
          // care.
          onClick={() => compactThread.mutate({ keep_turns: null })}
        >
          Condense earlier turns
        </Button>
      ) : null}
    </div>
  );
}
